import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import styles from './SelectSportScreen.module.css';

/**
 * The first step of adding a bolão to a circle (PLAN.md Phase 10.3, 10.18):
 * pick a sport / competition type before picking a specific competition.
 * Football, Custom Competition and Baby Shower are real; Cricket and
 * Formula 1 are deliberately inert rather than half-wired, since no
 * ruleset, provider or seed exists for either yet — nothing there should
 * look clickable.
 *
 * Adding another real sport later means adding a tile with `enabled: true`
 * and a route for it — this screen itself shouldn't need to change shape.
 *
 * Mounted only at `/groups/:id/bolaos/new` — the circle already exists by
 * the time anyone reaches this screen, whether that circle is brand new
 * (just created via `CreateCircleScreen`, with nothing in it yet) or
 * long-running and getting its second, third, etc. bolão. Circle creation
 * itself is a separate, earlier step that never touches a sport or
 * competition.
 */
interface SportTile {
  id: string;
  emoji: string;
  labelKey: string;
  /** i18n key for the one- or two-sentence pitch under the tile's label. */
  descriptionKey: string;
  /**
   * A bulleted "here's what this could look like" list under the
   * description. Not every tile has one — football and Formula 1 are just
   * the description sentence; cricket and custom competition earn a list
   * because the description alone doesn't convey the range on offer.
   */
  suggestions: string[];
  /** Whether to show the "Suggestions:" label above the list (custom only — cricket's list needs no heading). */
  suggestionsHeading: boolean;
  enabled: boolean;
  /**
   * The route segment to continue to once selected — appended to
   * `/groups/:id/bolaos/new/`. Unused while `enabled` is false.
   */
  pathSuffix: string;
}

const TILES: SportTile[] = [
  {
    id: 'football',
    emoji: '⚽',
    labelKey: 'selectSport.football.label',
    descriptionKey: 'selectSport.football.description',
    suggestions: [],
    suggestionsHeading: false,
    enabled: true,
    pathSuffix: 'football',
  },
  {
    id: 'cricket',
    emoji: '🏏',
    labelKey: 'selectSport.cricket.label',
    descriptionKey: 'selectSport.cricket.description',
    suggestions: [
      'selectSport.cricket.suggestion1',
      'selectSport.cricket.suggestion2',
      'selectSport.cricket.suggestion3',
    ],
    suggestionsHeading: false,
    enabled: false,
    pathSuffix: '',
  },
  {
    id: 'formula1',
    emoji: '🏎️',
    labelKey: 'selectSport.formula1.label',
    descriptionKey: 'selectSport.formula1.description',
    suggestions: [],
    suggestionsHeading: false,
    enabled: false,
    pathSuffix: '',
  },
  {
    // A preconfigured custom competition, not a sport (PLAN.md Phase 10.11):
    // picking it opens the ordinary custom-competition screen with the
    // gender question already filled in — card format, two coloured answers,
    // winner/loser scoring — all of which the Custom Competition tile below
    // can also produce by hand. It earns its own tile because "baby shower"
    // is what someone is actually looking for, not "custom competition,
    // multiple choice, card format".
    id: 'baby-shower',
    emoji: '🍼',
    labelKey: 'selectSport.babyShower.label',
    descriptionKey: 'selectSport.babyShower.description',
    suggestions: ['selectSport.babyShower.suggestion1', 'selectSport.babyShower.suggestion2'],
    suggestionsHeading: false,
    enabled: true,
    pathSuffix: 'baby-shower',
  },
  {
    id: 'custom',
    emoji: '🎲',
    labelKey: 'selectSport.custom.label',
    descriptionKey: 'selectSport.custom.description',
    suggestions: [
      'selectSport.custom.suggestion1',
      'selectSport.custom.suggestion2',
      'selectSport.custom.suggestion3',
      'selectSport.custom.suggestion4',
    ],
    suggestionsHeading: true,
    enabled: true,
    pathSuffix: 'custom',
  },
];

function SportTileCard({
  tile,
  selected,
  onSelect,
}: {
  tile: SportTile;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  // Visible by default — collapsing is a "make this tile smaller" affordance,
  // not something a first-time visitor has to discover to see the pitch.
  const [detailsOpen, setDetailsOpen] = useState(true);

  return (
    <div
      className={`${styles.tile} ${selected ? styles.tileSelected : ''} ${!tile.enabled ? styles.tileDisabled : ''}`}
    >
      <button
        type="button"
        className={styles.tileMain}
        disabled={!tile.enabled}
        aria-pressed={selected}
        onClick={onSelect}
      >
        {!tile.enabled ? (
          <span className={styles.comingSoonBadge}>{t('selectSport.comingSoon')}</span>
        ) : null}
        <span className={styles.emoji}>{tile.emoji}</span>
        <span className={styles.label}>{t(tile.labelKey)}</span>
      </button>

      <div className={styles.examplesHeader}>
        <button
          type="button"
          className={styles.examplesToggle}
          aria-expanded={detailsOpen}
          aria-label={t(detailsOpen ? 'selectSport.hideDetails' : 'selectSport.showDetails')}
          onClick={() => setDetailsOpen((open) => !open)}
        >
          <span className={`${styles.chevron} ${detailsOpen ? styles.chevronOpen : ''}`} />
        </button>
      </div>

      {detailsOpen ? (
        <div className={styles.details}>
          <p className={styles.description}>{t(tile.descriptionKey)}</p>
          {tile.suggestions.length > 0 ? (
            <>
              {tile.suggestionsHeading ? (
                <p className={styles.suggestionsHeading}>{t('selectSport.suggestionsHeading')}</p>
              ) : null}
              <ul className={styles.examplesList}>
                {tile.suggestions.map((key) => (
                  <li key={key} className={styles.exampleItem}>
                    <span className={styles.exampleBullet} />
                    <span>{t(key)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function SelectSportScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedTile = TILES.find((tile) => tile.id === selectedId) ?? null;
  const canContinue = selectedTile !== null && selectedTile.enabled;
  const basePath = `/groups/${id}/bolaos/new`;

  return (
    <main className="screen">
      <Link to={`/groups/${id}`} className="muted">
        {t('common.back')}
      </Link>
      <h1>{t('selectSport.heading')}</h1>

      <div className={styles.grid}>
        {TILES.map((tile) => (
          <SportTileCard
            key={tile.id}
            tile={tile}
            selected={selectedId === tile.id}
            onSelect={() => {
              if (!tile.enabled) return;
              setSelectedId(tile.id);
            }}
          />
        ))}
      </div>

      <button
        type="button"
        className="button button-primary"
        disabled={!canContinue}
        onClick={() => {
          if (selectedTile) void navigate(`${basePath}/${selectedTile.pathSuffix}`);
        }}
      >
        {t('selectSport.continue')}
      </button>
    </main>
  );
}
